/**
 * Admin V3 — Devis create form (inline panel)
 *
 * POST /admin/devis (ADMIN_PARTNER_UPDATE) → creates a DRAFT devis.
 * Customer requires name OR company; at least one line item ({description, amount}).
 * Sending (legal number) is a separate explicit action on the detail page.
 */

import React, { useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const EMPTY_CUSTOMER = { name: '', company: '', address: '', email: '', vat: '' };
const EMPTY_LINE = { description: '', amount: '' };

const DevisCreateForm = ({ onCreated, onCancel }) => {
  const locale = DEFAULT_LOCALE;
  const { showSuccess, showError } = useToast();
  const createM = useAdminMutation('/admin/devis', 'post');

  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [taxRate, setTaxRate] = useState(10);
  const [validDays, setValidDays] = useState(30);
  const [notes, setNotes] = useState('');
  // Part E (operator UX safety): durable error display + explained disabled state.
  const [errorMsg, setErrorMsg] = useState(null);

  const setCustomerField = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));
  const setLine = (i, k) => (e) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));
  const addLine = () => setLines((ls) => [...ls, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const cleanLines = lines
    .filter((l) => l.description.trim() && l.amount !== '')
    .map((l) => ({ description: l.description.trim(), amount: Number(l.amount) || 0 }));
  const hasCustomer = Boolean(customer.name.trim() || customer.company.trim());
  const hasLine = cleanLines.length > 0;
  const valid = hasCustomer && hasLine;
  // What's missing (shown when the submit is disabled — never a silent block).
  const missing = [
    !hasCustomer && t('devis.create.req.customer', locale),
    !hasLine && t('devis.create.req.line', locale),
  ].filter(Boolean);

  const handleSubmit = () => {
    setErrorMsg(null);
    const body = {
      customer: {
        name: customer.name.trim(), company: customer.company.trim(),
        address: customer.address.trim(), email: customer.email.trim(), vat: customer.vat.trim(),
      },
      line_items: cleanLines,
      indicative_tax_rate: Number(taxRate) || 0,
      valid_days: Number(validDays) || 30,
      notes: notes.trim(),
    };
    createM.mutate(body, {
      onSuccess: (res) => {
        showSuccess(t('devis.create.ok', locale));
        const id = res?.devis?.id || res?.id;
        if (id && onCreated) onCreated(id);
      },
      onError: (e) => {
        const m = e?.response?.data?.detail || t('devis.create.error', locale);
        setErrorMsg(m);   // durable inline error (not only a disappearing toast)
        showError(m);
      },
    });
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-4" data-testid="adminv3-devis-create-form">
      <p className="text-[12px] text-slate-500">{t('devis.create.hint', locale)}</p>
      <p className="text-[11px] text-slate-400">{t('devis.create.requiredHint', locale)}</p>
      {errorMsg && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700"
             data-testid="adminv3-devis-create-error">
          {errorMsg}
        </div>
      )}

      {/* Customer */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('devis.create.customer', locale)} <span className="text-rose-500">*</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input placeholder={t('devis.create.customerName', locale)} value={customer.name} onChange={setCustomerField('name')}
                 className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-devis-customer-name" />
          <input placeholder={t('devis.create.customerCompany', locale)} value={customer.company} onChange={setCustomerField('company')}
                 className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          <input placeholder={t('devis.create.customerEmail', locale)} value={customer.email} onChange={setCustomerField('email')}
                 className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          <input placeholder={t('devis.create.customerVat', locale)} value={customer.vat} onChange={setCustomerField('vat')}
                 className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          <input placeholder={t('devis.create.customerAddress', locale)} value={customer.address} onChange={setCustomerField('address')}
                 className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 md:col-span-2" />
        </div>
      </div>

      {/* Line items */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('devis.create.lineItems', locale)} <span className="text-rose-500">*</span></div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input placeholder={t('devis.create.lineDesc', locale)} value={l.description} onChange={setLine(i, 'description')}
                     className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid={`adminv3-devis-line-desc-${i}`} />
              <input type="number" step="0.01" placeholder={t('devis.create.lineAmount', locale)} value={l.amount} onChange={setLine(i, 'amount')}
                     className="w-32 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid={`adminv3-devis-line-amount-${i}`} />
              <button type="button" onClick={() => removeLine(i)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                      title={t('devis.create.removeLine', locale)}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={addLine} className="mt-2" data-testid="adminv3-devis-add-line">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{t('devis.create.addLine', locale)}
        </Button>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label className="text-[12px] font-semibold text-slate-600">{t('devis.create.taxRate', locale)}</label>
          <input type="number" min="0" max="100" step="0.1" value={taxRate} onChange={(e) => setTaxRate(e.target.value)}
                 className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
        </div>
        <div>
          <label className="text-[12px] font-semibold text-slate-600">{t('devis.create.validDays', locale)}</label>
          <input type="number" min="1" max="365" value={validDays} onChange={(e) => setValidDays(e.target.value)}
                 className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
        </div>
        <div>
          <label className="text-[12px] font-semibold text-slate-600">{t('devis.create.notes', locale)}</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button variant="default" size="sm" onClick={handleSubmit} disabled={createM.loading || !valid}
                data-testid="adminv3-devis-create-submit">
          {createM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {t('devis.create.submit', locale)}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>{t('devis.create.cancel', locale)}</Button>
        {!valid && missing.length > 0 && (
          <span className="text-[12px] text-amber-700" data-testid="adminv3-devis-create-disabled-reason">
            {t('devis.create.disabledReason', locale)} {missing.join(', ')}.
          </span>
        )}
      </div>
    </div>
  );
};

export default DevisCreateForm;
